/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo } from "react";
import { View, Text, TextInput, StyleSheet, Platform, ScrollView, Dimensions } from "react-native";
import { useSelector } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHOverlay from "../../../../components/LHOverlay";
import LhButton from "../../../../components/LhButton";
import CrossPlatformDropdown from "../../../../components/CrossPlatformDropdown";
import LhDatePicker from "../../../../components/LhDatePicker";
import { Employee } from "../../../../store/slices/employeeUpdatesSlice";
import FontFamily from "../../../../constants/fonts";
import { useTheme } from "../../../../theme/useTheme";

interface EmployeeEditOverlayProps {
  showOverlay: boolean;
  setShowOverlay: (show: boolean) => void;
  editEmployee: Employee | null;
  setEditEmployee: (employee: Employee | null) => void;
  statusOptions: { id: string; displayText: string }[];
  managerOptions: { id: string; displayText: string }[];
  handleDelete: (id: number) => Promise<{ success: boolean; message: string }>;
  handleSaveEdit: () => Promise<{ success: boolean; message: string }>;
  deleteLoading?: boolean;
  deleteError?: string | null;
}

const EmployeeEditOverlay: React.FC<EmployeeEditOverlayProps> = ({
  showOverlay,
  setShowOverlay,
  editEmployee,
  setEditEmployee,
  statusOptions,
  managerOptions,
  handleDelete,
  handleSaveEdit,
  deleteLoading = false,
  deleteError = null,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Get roles from Redux store
  const { roles: appDataRoles } = useSelector((state: any) => state.appData);

  // Add local states to track UI flow
  const [hasChanges, setHasChanges] = React.useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false);
  const [deleteSuccess, setDeleteSuccess] = React.useState(false);
  const [deleteFailed, setDeleteFailed] = React.useState(false);
  const [deleteMessage, setDeleteMessage] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);

  // States for update flow
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [updateSuccess, setUpdateSuccess] = React.useState(false);
  const [updateFailed, setUpdateFailed] = React.useState(false);
  const [updateMessage, setUpdateMessage] = React.useState("");
  const [formDisabled, setFormDisabled] = React.useState(false);

  // Create job description dropdown options from roles data
  const jobDescriptionOptions = useMemo(() => {
    if (!appDataRoles || !Array.isArray(appDataRoles)) {
      console.log("No roles data found in appDataRoles:", appDataRoles);
      return [];
    }

    const roleOptions = appDataRoles
      .filter((role: any) => role.ROLE_NAME && role.ROLE_ID)
      .map((role: any) => ({
        id: role.ROLE_ID.toString(),
        displayText: role.ROLE_NAME,
      }))
      .sort((a: any, b: any) => a.displayText.localeCompare(b.displayText));

    return roleOptions;
  }, [appDataRoles]);

  // Reset the states when the overlay is opened/closed
  React.useEffect(() => {
    if (showOverlay) {
      setHasChanges(false);
      setShowDeleteConfirmation(false);
      setDeleteSuccess(false);
      setDeleteFailed(false);
      setDeleteMessage("");
      setIsDeleting(false);
      setIsUpdating(false);
      setUpdateSuccess(false);
      setUpdateFailed(false);
      setUpdateMessage("");
      setFormDisabled(false);

      // Log the current date values for debugging
      if (editEmployee) {
        console.log(
          "Current hire date:",
          editEmployee.hireDate,
          "type:",
          typeof editEmployee.hireDate
        );
        console.log(
          "Current graduation date:",
          editEmployee.graduationDate,
          "type:",
          typeof editEmployee.graduationDate
        );
      }
    }
  }, [showOverlay, editEmployee]);

  // Auto-close overlay after successful update
  React.useEffect(() => {
    if (updateSuccess) {
      const timer = setTimeout(() => {
        setShowOverlay(false);
      }, 3000); // Close after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [updateSuccess, setShowOverlay]);

  // Helper function to update employee data and track changes
  const updateEmployeeField = (field: string, value: any) => {
    if (editEmployee) {
      // For date fields, handle special formatting
      let formattedValue = value;

      if (field === "hireDate" || field === "graduationDate") {
        // If it's a Date object, format it properly for Redux
        if (value instanceof Date) {
          // Store as Date object for the date picker component
          formattedValue = value;
          console.log(`Setting ${field} to Date object:`, formattedValue);
        } else if (value === null) {
          // If value is null, we'll store an empty string for consistency
          formattedValue = "";
          console.log(`Clearing ${field}`);
        }
      } else if (field === "gpid") {
        // Ensure GPID is always properly formatted
        formattedValue = validateAndFormatGpid(value);
        console.log(`GPID field updated: "${value}" -> "${formattedValue}"`);
      }

      const updatedEmployee = {
        ...editEmployee,
        [field]: formattedValue,
      };

      // Update the employee in Redux
      setEditEmployee(updatedEmployee);
      setHasChanges(true);
    }
  };

  // Function to attempt to parse a date string into a Date object
  const tryParseDate = (dateString: string | null | undefined): Date | null => {
    if (
      !dateString ||
      dateString === "N/A" ||
      dateString === "" ||
      dateString === "null" ||
      dateString === "undefined"
    ) {
      return null;
    }

    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (error) {
      console.error(`Error parsing date: ${dateString}`, error);
      return null;
    }
  };

  // Treat "mobile" as strictly iPhone (not iPad). Use width threshold to
  // distinguish iPhone from iPad when Platform.OS === 'ios'.
  const isMobilePhone = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth < 768;
  };

  // Helper to detect iPad (width >= 768 on iOS)
  const isIpad = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth >= 768;
  };

  // Deprecated: previously "isMobile" included Android and iPad. Replace usage
  // with `isMobilePhone()` where iPhone-only behavior is required. For
  // non-phone mobile detection, use Platform.OS === 'android' or explicit
  // helpers.
  const isMobile = isMobilePhone();

  // Helper function to check if error is authentication related
  const isAuthenticationError = (error: any): boolean => {
    if (!error) {
      return false;
    }

    const errorMessage = String(error.message || error);
    const statusCode = error.response?.status;

    return (
      statusCode === 401 ||
      errorMessage.includes("401") ||
      errorMessage.includes("Unauthorized") ||
      errorMessage.includes("Authentication required") ||
      errorMessage.includes("Token") ||
      errorMessage.includes("authentication") ||
      errorMessage.includes("MSAL")
    );
  };

  // GPID validation and formatting function
  const validateAndFormatGpid = (value: string): string => {
    // Remove any non-numeric characters (spaces, special characters, letters)
    const cleaned = value.replace(/[^0-9]/g, "");

    // Limit to 8 digits maximum
    const limited = cleaned.substring(0, 8);

    // Log for debugging if input was modified
    if (value !== limited && value.length > 0) {
      console.log(`GPID input cleaned: "${value}" -> "${limited}"`);
    }

    return limited;
  };

  const handleCloseAfterDelete = () => {
    setShowOverlay(false);
  };

  const handleCloseAfterUpdate = () => {
    setShowOverlay(false);
  };

  const handleDeleteConfirmation = async () => {
    if (!editEmployee) {
      return;
    }

    // Set deleting state to true - this will show the loader on the Yes button
    setIsDeleting(true);

    try {
      // Log employee details to debug the issue
      console.log("Deleting employee:", {
        id: editEmployee.id,
        gpid: editEmployee.gpid,
      });

      // Call the handleDelete function with the employee internal ID
      // The backend API has been updated to use internal ID
      // Ensure we pass a numeric ID
      const numericId = Number(editEmployee.id);
      console.log(
        "Deleting employee with ID:",
        numericId,
        "Type:",
        typeof numericId
      );
      const result = await handleDelete(numericId);

      // Handle successful deletion
      if (result.success) {
        setDeleteSuccess(true);
        setDeleteMessage(result.message || "Employee deleted successfully!");
      } else {
        // Handle failed deletion
        setDeleteFailed(true);
        setDeleteMessage(
          result.message || "Failed to delete employee. Please try again."
        );
      }
    } catch (error) {
      // Handle any unexpected errors
      console.error("Error deleting employee:", error);
      setDeleteFailed(true);

      // Check if it's an authentication error
      if (isAuthenticationError(error)) {
        setDeleteMessage(
          "Authentication session has expired. Please refresh the page and try again."
        );
      } else {
        setDeleteMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      // Reset the deleting state
      setIsDeleting(false);
    }
  };

  // Function to handle the update process with improved error handling
  const handleUpdateEmployee = async () => {
    if (!editEmployee) {
      return;
    }

    // Reset any previous update state
    setUpdateSuccess(false);
    setUpdateFailed(false);
    setUpdateMessage("");

    // Set updating state and disable form
    setIsUpdating(true);
    setFormDisabled(true); // Disable the form to prevent further changes

    try {
      // Validate required fields before sending update
      if (
        !editEmployee.firstName ||
        !editEmployee.lastName ||
        !editEmployee.gpid
      ) {
        throw new Error(
          "Required fields are missing. Please fill in all required fields."
        );
      }

      // Validate GPID format
      if (editEmployee.gpid.length !== 8) {
        throw new Error("GPID must be exactly 8 digits long.");
      }

      if (!/^\d{8}$/.test(editEmployee.gpid)) {
        throw new Error("GPID must contain only numbers.");
      }

      // Ensure dates are in the right format for the backend
      const employeeToUpdate = { ...editEmployee };

      // Log the original dates for debugging
      console.log(
        "Original hire date:",
        employeeToUpdate.hireDate,
        "Type:",
        typeof employeeToUpdate.hireDate
      );
      console.log(
        "Original graduation date:",
        employeeToUpdate.graduationDate,
        "Type:",
        typeof employeeToUpdate.graduationDate
      );

      // Log the employee data being updated
      console.log("Updating employee:", employeeToUpdate);

      // Call the handleSaveEdit function provided via props
      const result = await handleSaveEdit();

      // Handle successful update
      if (result.success) {
        setUpdateSuccess(true);
        setUpdateMessage(result.message || "Employee updated successfully!");
        setHasChanges(false); // Reset changes flag
      } else {
        // Handle failed update
        setUpdateFailed(true);
        setUpdateMessage(
          result.message || "Failed to update employee. Please try again."
        );
      }
    } catch (error: any) {
      // Handle any unexpected errors
      console.error("Error updating employee:", error);
      setUpdateFailed(true);

      // Extract error message based on the response structure provided
      let errorMessage = "An unexpected error occurred. Please try again.";

      // Check if it's an authentication error
      if (isAuthenticationError(error)) {
        errorMessage =
          "Authentication session has expired. Please refresh the page and try again.";
        console.log("Authentication error detected during employee update");
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        // Check for API error response structure { message: "Error updating employee by ID" }
        if (error.message) {
          errorMessage = error.message;
        } else if (error.response?.data?.message) {
          // Handle axios error structure
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 401) {
          errorMessage =
            "Authentication session has expired. Please refresh the page and try again.";
        }
      }

      setUpdateMessage(errorMessage);
    } finally {
      setIsUpdating(false);

      // Only re-enable the form on failure, not on success
      if (!updateSuccess) {
        setFormDisabled(false);
      }
    }
  };

  // Helper function to render the form content
  const renderFormContent = () => {
    const content = (
      <View style={[
        isMobilePhone() ? styles.mobileFullScreenContent : undefined,
        isDark && isMobilePhone() ? { backgroundColor: colors.background.page } : undefined,
      ]}>
        {isMobilePhone() && (
          <View style={styles.mobileHeader}>
            <LhButton
              text="← Back"
              variant="secondary"
              size="small"
              onPress={() => setShowOverlay(false)}
              style={styles.mobileBackButton}
              textStyle={[styles.mobileBackButtonText, isDark ? { color: colors.brand.primary } : undefined]}
            />
            <Text style={[styles.mobileTitle, isDark ? { color: colors.text.primary } : undefined]}>
              {showDeleteConfirmation ? 'Remove Employee' : 'Edit Employee'}
            </Text>
          </View>
        )}

      {/* Success Notification Bar */}
      {updateSuccess && (
        <View style={[styles.successMessageContainer, isDark ? { backgroundColor: "#1a3d27", borderColor: "#2a5c3a" } : undefined]}>
          <MaterialIcons
            name="check-circle"
            size={24}
            color="#008242"
            style={styles.successIcon}
          />
          <Text style={[styles.successMessageText, isDark ? { color: "#6fcf97" } : undefined]}>{updateMessage}</Text>
        </View>
      )}

      {/* Error banner for update failure - moved to the top for better visibility */}
      {updateFailed &&
        !updateSuccess &&
        !deleteSuccess &&
        !deleteFailed &&
        !showDeleteConfirmation &&
        editEmployee && (
          <View style={[styles.errorBanner, isDark ? { backgroundColor: "#3d1a1a", borderColor: "#5c2a2a" } : undefined]}>
            <Text style={[styles.errorTitle, isDark ? { color: "#ff6b6b" } : undefined]}>Update Failed</Text>
            <Text style={[styles.errorMessageUpdate, isDark ? { color: "#ffaaaa" } : undefined]}>{updateMessage}</Text>
            {isAuthenticationError({ message: updateMessage }) && (
              <LhButton
                text="Refresh Page"
                onPress={() => {
                  if (Platform.OS === "web") {
                    window.location.reload();
                  } else {
                    setShowOverlay(false);
                  }
                }}
                variant="primary"
                style={{
                  marginTop: 10,
                  backgroundColor: "#007bff",
                  alignSelf: "flex-start",
                }}
              />
            )}
          </View>
        )}

      {/* Success state after deletion */}
      {deleteSuccess && editEmployee && (
        <View style={[styles.messageContainer, isDark ? { backgroundColor: colors.background.page } : undefined]}>
          <Text style={[styles.successMessage, isDark ? { color: "#6fcf97" } : undefined]}>{deleteMessage}</Text>
          <View style={styles.centeredButtonContainer}>
            <LhButton
              text="Close"
              onPress={handleCloseAfterDelete}
              variant="primary"
              style={styles.centeredButton}
            />
          </View>
        </View>
      )}

      {/* Error state after deletion attempt */}
      {deleteFailed && editEmployee && (
        <View style={[styles.messageContainer, isDark ? { backgroundColor: colors.background.page } : undefined]}>
          <Text style={[styles.errorMessage, isDark ? { color: "#ff6b6b" } : undefined]}>{deleteMessage}</Text>
          <View style={styles.centeredButtonContainer}>
            <LhButton
              text="Close"
              onPress={handleCloseAfterDelete}
              variant="primary"
              style={styles.centeredButton}
            />
          </View>
        </View>
      )}

      {/* Confirmation dialog when Remove EE is clicked */}
      {showDeleteConfirmation &&
        editEmployee &&
        !deleteSuccess &&
        !deleteFailed && (
          <View style={[styles.messageContainer, isDark ? { backgroundColor: colors.background.page } : undefined]}>
            <Text style={[styles.confirmationMessage, isDark ? { color: colors.text.primary } : undefined]}>
              Are you sure you want to terminate this employee? This action cannot
              be undone.
            </Text>
            <View
              style={[
                styles.confirmationButtonContainer,
                // Ensure no wrapping on iPhone — prevent other code from forcing wrap
                isMobilePhone() && { flexWrap: "nowrap", justifyContent: "flex-end", width: "25%", gap: 10, marginLeft: 300 },
                // On iPad, limit the container width so buttons stay compact and right-aligned
                isIpad() && { width: "25%", justifyContent: "flex-end", gap: 10, marginLeft: 500 },
              ]}
            >
              
              <LhButton
                text="Cancel"
                onPress={() => setShowDeleteConfirmation(false)}
                variant="primary"
                style={{
                  ...styles.cancelButton,
                  ...(isMobilePhone() ? styles.cancelButtonMobile : {}),
                  ...(isDeleting ? styles.disabledButton : {}),
                }}
                textStyle={isMobilePhone() ? { fontSize: 12 } : undefined}
                disabled={isDeleting}
              />
              <LhButton
                text={isDeleting ? "" : "Remove"}
                isLoading={isDeleting}
                onPress={handleDeleteConfirmation}
                variant="primary"
                style={{
                  ...styles.confirmButton,
                  ...(isMobilePhone() ? styles.confirmButtonMobile : {}),
                  ...(isDeleting ? styles.disabledButton : {}),
                }}
                textStyle={isMobilePhone() ? { fontSize: 12 } : undefined}
                disabled={isDeleting}
              />
            </View>
          </View>
        )}

      {/* Normal edit view - only show if not in delete confirmation, success or error states */}
      {editEmployee &&
        !showDeleteConfirmation &&
        !deleteSuccess &&
        !deleteFailed && (
          <View style={isDark ? { backgroundColor: colors.background.page } : undefined}>
            <View
              style={isMobile ? styles.columnContainer : [styles.rowContainer, { gap: 16 }]}
            >
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>GPID</Text>
                <TextInput
                  style={[
                    styles.input,
                    formDisabled && styles.disabledInput,
                    isDark ? { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary } : undefined,
                    formDisabled && isDark ? { backgroundColor: colors.background.elevated, opacity: 0.7 } : undefined,
                  ]}
                  value={editEmployee.gpid}
                  onChangeText={(text) => updateEmployeeField("gpid", text)}
                  editable={!formDisabled}
                  keyboardType="numeric"
                  inputMode="numeric"
                  maxLength={8}
                  autoComplete="off"
                  placeholder="Enter 8-digit GPID"
                  placeholderTextColor={isDark ? colors.text.disabled : "#6C757D"}
                  {...(Platform.OS === "web" && {
                    pattern: "[0-9]*",
                    inputMode: "numeric" as any,
                  })}
                />
              </View>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>First Name</Text>
                <TextInput
                  testID="first-name-input"
                  style={[
                    styles.input,
                    formDisabled && styles.disabledInput,
                    isDark ? { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary } : undefined,
                    formDisabled && isDark ? { backgroundColor: colors.background.elevated, opacity: 0.7 } : undefined,
                  ]}
                  value={editEmployee.firstName}
                  onChangeText={(text) =>
                    updateEmployeeField("firstName", text)
                  }
                  editable={!formDisabled}
                />
              </View>
            </View>

            <View
              style={isMobile ? styles.columnContainer : [styles.rowContainer, { gap: 16 }]}
            >
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>Last Name</Text>
                <TextInput
                  style={[
                    styles.input,
                    formDisabled && styles.disabledInput,
                    isDark ? { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary } : undefined,
                    formDisabled && isDark ? { backgroundColor: colors.background.elevated, opacity: 0.7 } : undefined,
                  ]}
                  value={editEmployee.lastName}
                  onChangeText={(text) => updateEmployeeField("lastName", text)}
                  editable={!formDisabled}
                />
              </View>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>Status</Text>
                <CrossPlatformDropdown
                  childList={statusOptions}
                  label=""
                  placeholder="Select status"
                  selection="single"
                  isDisabled={formDisabled}
                  isDarkMode={isDark}
                  setSelectedValue={(item) => {
                    if (item && "displayText" in item && !formDisabled) {
                      updateEmployeeField("status", item.displayText || "");
                    }
                  }}
                  selectedValue={
                    statusOptions.find(
                      (option) =>
                        option.id === editEmployee.status ||
                        option.displayText === editEmployee.status
                    ) || null
                  }
                />
              </View>
            </View>

            <View
              style={isMobile ? styles.columnContainer : [styles.rowContainer, { gap: 16 }]}
            >
              <View style={[styles.inputGroup, { marginRight: isMobile ? 0 : 16 }]}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>Manager</Text>
                <CrossPlatformDropdown
                  childList={managerOptions || []}
                  placeholder="Select Manager"
                  isDarkMode={isDark}
                  selectedValue={
                    (managerOptions || []).find((option) => {
                      if (
                        editEmployee.manager &&
                        typeof editEmployee.manager === "object"
                      ) {
                        const managerObj = editEmployee.manager as Record<
                          string,
                          any
                        >;
                        if (managerObj.id && option.id === managerObj.id) {
                          return true;
                        }
                        if (
                          managerObj.displayText &&
                          option.displayText === managerObj.displayText
                        ) {
                          return true;
                        }
                      }
                      return (
                        option.id === editEmployee.manager ||
                        option.displayText === editEmployee.manager
                      );
                    }) || null
                  }
                  setSelectedValue={(item: any) => {
                    if (item) {
                      updateEmployeeField("manager", item.displayText);
                    }
                  }}
                  style={
                    formDisabled
                      ? { ...styles.disabledInput, minWidth: 0, width: '100%', height: 44 }
                      : { minWidth: 0, width: '100%', height: 44 }
                  }
                  isDisabled={formDisabled}
                />
              </View>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>Job Description</Text>
                <CrossPlatformDropdown
                  childList={jobDescriptionOptions}
                  placeholder="Select Job Description"
                  isDarkMode={isDark}
                  selectedValue={
                    jobDescriptionOptions.find(
                      (option) =>
                        option.displayText === editEmployee.jobDescription
                    ) || null
                  }
                  setSelectedValue={(item: any) => {
                    if (item) {
                      updateEmployeeField("jobDescription", item.displayText);
                    }
                  }}
                  style={
                    formDisabled
                      ? { ...styles.disabledInput, minWidth: 0, width: '100%', height: 44 }
                      : { minWidth: 0, width: '100%', height: 44 }
                  }
                  isDisabled={formDisabled}
                />
              </View>
            </View>

            <View
              style={isMobile ? styles.columnContainer : [styles.rowContainer, { gap: 16 }]}
            >
              <View style={[styles.inputGroup, { marginRight: isMobile ? 0 : 16 }]}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>Hire Date</Text>
                <LhDatePicker
                  value={
                    typeof editEmployee.hireDate === "string"
                      ? tryParseDate(editEmployee.hireDate)
                      : (editEmployee.hireDate as Date | null)
                  }
                  onChange={(date: Date | null) =>
                    !formDisabled && updateEmployeeField("hireDate", date)
                  }
                  placeholder="Select Hire Date"
                  disabled={formDisabled}
                  iconColor={isDark ? "#FFFFFF" : "#333333"}
                  colorScheme={isDark ? "dark" : "light"}
                  style={isDark ? undefined : {
                    pickerButtonStyle: { backgroundColor: "#ffffff", borderColor: "#ced4da" },
                    dateTextStyle: { color: "#333333" },
                    placeholderTextStyle: { color: "#999999" },
                  }}
                />
              </View>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, isDark ? { color: colors.text.secondary } : undefined]}>Graduation Date</Text>
                <LhDatePicker
                  value={
                    typeof editEmployee.graduationDate === "string"
                      ? tryParseDate(editEmployee.graduationDate)
                      : (editEmployee.graduationDate as Date | null)
                  }
                  onChange={(date: Date | null) =>
                    !formDisabled && updateEmployeeField("graduationDate", date)
                  }
                  placeholder="Select Graduation Date"
                  disabled={formDisabled}
                  iconColor={isDark ? "#FFFFFF" : "#333333"}
                  colorScheme={isDark ? "dark" : "light"}
                  style={isDark ? undefined : {
                    pickerButtonStyle: { backgroundColor: "#ffffff", borderColor: "#ced4da" },
                    dateTextStyle: { color: "#333333" },
                    placeholderTextStyle: { color: "#999999" },
                  }}
                />
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <View style={styles.leftButtonGroup}>
                <LhButton
                  text="Remove Employee"
                  onPress={() => {
                    setShowDeleteConfirmation(true);
                  }}
                  variant="secondary"
                  style={{
                    backgroundColor: "transparent",
                    borderWidth: 0,
                    width: isMobilePhone() ? 130 : 150, // Compact on iPhone
                    minWidth: isMobilePhone() ? 100 : "auto",
                    paddingHorizontal: isMobilePhone() ? 2 : 10,
                    ...(formDisabled || isUpdating ? styles.disabledButton : {}),
                  }}
                  textStyle={{
                    color: "#dc3545", // red — intentional status color, keep in dark too
                    fontWeight: "700",
                    fontStyle: "normal",
                    ...Platform.select({
                      ios: { fontSize: isMobilePhone() ? 12 : 14 }, // Smaller font on iPhone
                      android: { fontSize: 14 },
                      web: { color: "#dc3545" }, // explicit override prevents global dark-mode CSS from turning it white
                    }),
                  }}
                  disabled={formDisabled || isUpdating}
                />
              </View>

              <View style={styles.rightButtonGroup}>
                <LhButton
                  text="Cancel"
                  onPress={() => { setShowDeleteConfirmation(false); setShowOverlay(false); }}
                  variant="secondary"
                  style={{
                    backgroundColor: "transparent",
                    borderWidth: 0,
                    marginRight: 0,
                    width: isMobilePhone() ? 80 : 150, // Compact on iPhone
                    paddingHorizontal: isMobilePhone() ? 2 : 10,
                    ...(formDisabled || isUpdating ? styles.disabledButton : {}),
                  }}
                  textStyle={{
                    color: isDark ? colors.brand.primary : "#005CBC",
                    fontWeight: "700",
                    fontStyle: "normal",
                    ...Platform.select({
                      ios: { fontSize: isMobilePhone() ? 14 : 14 }, // Smaller font on iPhone
                      android: { fontSize: 14 },
                      web: {}
                    }),
                  }}
                />

                <LhButton
                  text={isUpdating ? "" : hasChanges ? "Update Employee *" : "Update Employee"}
                  isLoading={isUpdating}
                  onPress={() => {
                    if (editEmployee && !isUpdating) {
                      console.log(
                        "Update Employee button clicked, employee data:",
                        editEmployee
                      );
                      handleUpdateEmployee();
                    }
                  }}
                  variant="primary"
                  style={{
                    width: isMobilePhone() ? 120 : 150, // Compact on iPhone
                    paddingHorizontal: isMobilePhone() ? 2 : 10,
                    //backgroundColor: hasChanges ? "#00aa00" : "green",
                    ...(formDisabled && !isUpdating ? styles.disabledButton : {}),
                  }}
                  textStyle={{
                    fontSize: isMobilePhone() ? 12 : 14, // Smaller font on iPhone
                  }}
                  disabled={formDisabled && !isUpdating}
                />
              </View>
            </View>
          </View>
        )}
      </View>
    );

    // Wrap in ScrollView for iPhone to enable scrolling
    if (isMobilePhone()) {
      return (
        <ScrollView
          style={[styles.mobileScrollView, isDark ? { backgroundColor: colors.background.page } : undefined]}
          contentContainerStyle={styles.mobileScrollViewContent}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
        >
          {content}
        </ScrollView>
      );
    }

    return content;
  };

  return (
    <>
      {/* Conditional rendering: Full-screen on iPhone, LHOverlay on other devices */}
      {isMobilePhone() && showOverlay ? (
        // iPhone: Render content directly without LHOverlay (full-screen)
        renderFormContent()
      ) : (
        // Tablet/Web: Render content with LHOverlay
        <LHOverlay
          visible={showOverlay}
          onDismiss={() => setShowOverlay(false)}
          title={showDeleteConfirmation ? 'Remove Employee' : 'Edit Employee'}
        >
          {renderFormContent()}
        </LHOverlay>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  sectionHeader: {
    fontSize: Platform.select({ web: 14, default: 14 }),
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    paddingHorizontal: 10,
    color: "#333",
  },
  dropdownStyle: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    marginBottom: 10,
    marginHorizontal: Platform.select({ web: 4, default: 0 }),
    zIndex: 10030,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: Platform.select({ web: 10, default: 8 }),
    marginBottom: 10,
    marginHorizontal: Platform.select({ web: 4, default: 0 }),
    fontSize: Platform.select({ web: 16, default: 14 }),
  },
  buttonContainer: {
    flexDirection: "row", // Always use row for all devices including iPhone
    gap: 10,
    justifyContent: "space-between",
    marginVertical: 15,
    paddingHorizontal: 5,
    alignItems: "center",
    flexWrap: "nowrap", // Prevent wrapping to keep all buttons on same line
  },
  leftButtonGroup: {
    alignItems: "flex-start",
    marginLeft: Platform.OS === "web" ? 0 : 0, // Remove left margin on mobile
    flex: 0, // Don't allow to grow
  },
  rightButtonGroup: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginRight: 0,
    gap: 10, // Add gap between Cancel and Update buttons
    flex: 0, // Don't allow to grow
  },
  rowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  columnContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    marginBottom: 10,
  },
  inputGroup: {
    flex: 1,
    marginHorizontal: 5,
  },
  // Styles for delete confirmation, success, and error messages
  messageContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  confirmationMessage: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 20,
    color: "#333",
  },
  successMessage: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 20,
    color: "#28a745",
    fontWeight: "bold",
  },
  errorMessage: {
    textAlign: "center",
    marginBottom: 20,
    color: "#dc3545",
    fontWeight: "bold",
  },
  centeredButtonContainer: {
    marginTop: 20,
    alignItems: "center",
  },
  centeredButton: {
    minWidth: 120,
    backgroundColor: "#005cbc",
  },
  confirmationButtonContainer: {
    flexDirection: "row",
    // Align the confirmation buttons to the right edge across platforms
    justifyContent: "flex-end",
    alignItems: "center",
    // Use full width but push children to the right. On mobile avoid wrapping.
    width: "100%",
    marginTop: 0,
    paddingRight: 10,
    flexWrap: "nowrap",
    marginLeft: 0,
    gap: 10,
  },
  confirmButton: {
    minWidth: 100,
    backgroundColor: "#dc3545",
    // remove right margin so spacing is controlled explicitly
    marginRight: 0,
  },
  // Compact confirmation button styles for iPhone
  confirmButtonMobile: {
    minWidth: 100,
    paddingHorizontal: 8,
    height: 36,
    borderRadius: 24,
  },
  cancelButton: {
    minWidth: 100,
    backgroundColor: "#6c757d",
    // add a small left margin so buttons aren't flush together when right-aligned
    marginLeft: 10,
  },
  cancelButtonMobile: {
    minWidth: 100,
    paddingHorizontal: 8,
    height: 36,
    borderRadius: 24,
    marginLeft: 8,
  },
  disabledButton: {
    opacity: 0.6,
  },
  disabledInput: {
    backgroundColor: "#f5f5f5",
    opacity: 0.7,
  },
  successIcon: {
    marginRight: 8,
  },
  errorBanner: {
    backgroundColor: "#ffeeee",
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
    marginTop: 5,
    borderColor: "#ffcccc",
    borderWidth: 1,
    width: "100%",
  },
  errorTitle: {
    fontWeight: "bold",
    color: "#cc0000",
    marginBottom: 5,
  },
  errorMessageUpdate: {
    textAlign: "left",
    color: "#dc3545",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginTop: 5,
  },
  successMessageText: {
    color: "#155724",
    fontSize: 16,
    flex: 1,
  },
  // Mobile full-screen styles (matching EmployeeUpdatesHeader)
  mobileFullScreenContent: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 16,
    paddingTop: Platform.OS === "ios" ? 10 : 20,
  },
  mobileScrollView: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  mobileScrollViewContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  mobileHeader: {
    marginBottom: 16,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  mobileBackButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
    alignSelf: "flex-start",
    marginBottom: 8,
    paddingLeft: 0,
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  mobileBackButtonText: {
    color: "#005CBC",
    fontSize: 14,
    fontWeight: "400",
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#2D2D2D",
    marginBottom: 0,
    fontFamily: FontFamily.bold,
  },
});

export default EmployeeEditOverlay;
